import type { MembershipTierId, PriceMap } from "@/lib/pricing"
import { BLOOD_ADDONS } from "@/data/blood-addons"
import { MEMBERSHIP_FIRST_BLOOD_ADDONS } from "@/data/membership-first-blood-addons"
import type { BookingScan } from "../home/home-v4-data"

/**
 * Pricing v4 — the lineup as the plan-first booking flow sells it
 * (`booking-membership-first-spec.md`, 21.09): one Baseline plan at 1×, 2×
 * or 4× a year, then blood add-ons, then an Advanced or Pro MRI (no Core),
 * then imaging add-ons once an MRI is in. Today's one-time flow stays behind
 * a quiet link.
 *
 * THE PLAN TIERS are the membership's three rhythms. Their yearly prices are
 * Stripe's, from get-products' `membershipTiers` (price keys
 * `membershipOnce` / `membershipTwice` / `membershipFour`, see
 * `MEMBERSHIP_TIER_PRICE_KEYS` in lib/pricing.ts), read with
 * `membershipTierAmount`, which falls back to 299 / 499 / 899. Follow-ups a
 * year are the rhythm less the first test.
 *
 * EVERYTHING ELSE is live too: list prices from `fullPrices`, plan prices
 * from the feed's member prices (`member:<key>`), both via `usePlanPrices`.
 */
export const PLAN_TIERS = [
  { id: "once", followUps: 0 },
  { id: "twice", followUps: 1 },
  { id: "four", followUps: 3 },
] as const satisfies readonly { id: MembershipTierId; followUps: number }[]

/**
 * The two scans the plan flow offers. `scan` is the booking URL's value
 * (`mri` is the booking app's alias for Advanced). The price a plan holder
 * pays on top is the member price minus the plan's own price, because both
 * packages carry their own advanced blood test: the same netting as the
 * booking app's `bundleScanContributionCents` and home-v4's baseline card.
 */
export const MRI_PACKAGES = [
  // Core is back as the plain full-body MRI, Core's name gone (meeting,
  // 12.10): Full-body MRI, Full-body MRI Advanced, Full-body MRI Pro.
  { id: "core", priceKey: "core", scan: "core", bullets: 3 },
  { id: "advanced", priceKey: "advanced", scan: "mri", bullets: 4 },
  { id: "pro", priceKey: "pro", scan: "pro", bullets: 4 },
] as const satisfies readonly {
  id: string
  priceKey: keyof PriceMap
  scan: BookingScan
  bullets: number
}[]

/** Blood add-ons, in the live pricing page's order and copy (`pricing.*`),
 *  then the ones only the membership-first site sells. Since 13.10 the
 *  coeliac, heavy metals and trace elements tests are in `BLOOD_ADDONS` too
 *  (P-AHD-333), so a slug already there is not listed twice. */
export const PLAN_BLOOD_ADDONS = [
  ...BLOOD_ADDONS,
  ...MEMBERSHIP_FIRST_BLOOD_ADDONS.filter(
    (a) => !BLOOD_ADDONS.some((b) => b.slug === a.slug)
  ),
].map((a) => ({
  titleKey: a.titleKey,
  descKey: a.descKey,
}))

/**
 * Imaging add-ons that go with an MRI. Body composition is left out (the
 * full-body MRI already reports it) and gender health is a retired page.
 */
export const PLAN_IMAGING_ADDONS = [
  { titleKey: "addonDEXA", descKey: "addonDEXADesc" },
  { titleKey: "addonHeartCT", descKey: "addonHeartCTDesc" },
  { titleKey: "addonMammaMRI", descKey: "addonMammaMRIDesc" },
] as const
